import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, label } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../components/app/PageHeader";
import { DistChart } from "../components/app/DistChart";
import { Insights } from "./Dashboard";

const SECTIONS = [
  { id: "family", title: "Family & economy", charts: [["household_category", "Household type"], ["income_bracket", "Monthly household income"], ["employment_status", "Employment & livelihood (household head)"], ["education_level", "Educational attainment (household head)"], ["age_distribution", "Age distribution of household members"]] },
  { id: "health", title: "Health", charts: [["health_barriers", "Reported barriers to healthcare"], ["mental_health_support_needed", "Households asking for emotional / mental health support"]] },
  { id: "faith", title: "Faith participation", charts: [["mass_attendance", "Sunday Mass participation"], ["bible_service_attendance", "GKK Bible Service participation"], ["nonattendance_reasons", "Reasons for limited participation"]] },
  { id: "disaster", title: "Disaster & environment", charts: [["disaster_exposure", "Natural hazard exposure"], ["disaster_preparedness", "Household disaster preparedness"]] },
  { id: "needs", title: "Concerns & requests", charts: [["concerns", "Major concerns identified"], ["assistance_requested", "Pastoral assistance requested"]] },
];

const Filter = ({ id, value, onChange, options, placeholder, allLabel }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="bg-white" data-testid={`${id}-filter-select`}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-white max-h-80">
      <SelectItem value="all">{allLabel} {placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Trends = ({ trends }) => (
  <div className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid="trends-chart">
    <h3 className="font-semibold text-stone-800">Changes between survey periods (% of answering households)</h3>
    <p className="text-xs text-stone-500">Only compare periods that used the same questions. Periods with fewer than 5 households are hidden.</p>
    <div className="mt-4 h-72">
      <ResponsiveContainer>
        <LineChart data={trends}><CartesianGrid stroke="#F5F0E6" /><XAxis dataKey="period" /><YAxis unit="%" domain={[0, 100]} /><Tooltip /><Legend />
          <Line dataKey="weekly_mass_pct" name="Weekly Mass" stroke="#78350F" strokeWidth={2} />
          <Line dataKey="unemployed_pct" name="Unemployed head" stroke="#D97706" strokeWidth={2} />
          <Line dataKey="irregular_work_pct" name="Irregular / daily work" stroke="#0284C7" strokeWidth={2} />
          <Line dataKey="disaster_prepared_pct" name="Disaster-prepared" stroke="#285E3B" strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    <table className="mt-4 w-full text-sm"><thead><tr className="text-left text-stone-500"><th>Period</th><th>Households</th></tr></thead>
      <tbody>{trends.map((r) => <tr key={r.period} className="border-t border-amber-700/10"><td className="py-1">{r.period}</td><td className="font-mono-data">{r.households}</td></tr>)}</tbody></table>
  </div>
);

const Participation = ({ part }) => (
  <div className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6 overflow-x-auto" data-testid="participation-table">
    <p className="mb-3 text-xs text-stone-500">Listed by zone and GKK ID. This shows survey participation only — it is not a ranking of communities.</p>
    <table className="w-full text-sm">
      <thead><tr className="text-left text-stone-500"><th className="py-2">Zone</th><th>GKK</th><th>Households surveyed</th><th>Estimated households</th><th>Participation</th></tr></thead>
      <tbody>
        {[...part].sort((a, b) => `${a.zone}${a.code}`.localeCompare(`${b.zone}${b.code}`)).map((g) => [
          <tr key={g.gkk_id} className="border-t border-amber-700/15 font-semibold text-stone-800"><td className="py-2 text-xs text-stone-500">{g.zone || "—"}</td><td>{g.name}</td><td className="font-mono-data">{g.households}</td><td className="font-mono-data">{g.estimated_households ?? "not set"}</td><td>{g.rate !== null ? `${g.rate}%` : "unknown"}</td></tr>,
          ...g.units.map((u) => <tr key={u.unit_id} className="text-stone-600"><td /><td className="py-1 pl-5">{u.name}</td><td className="font-mono-data">{u.households}</td><td /><td /></tr>),
        ])}
      </tbody>
    </table>
  </div>
);

export default function Realities() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState({ gkk_id: params.get("gkk") || "", unit_id: "", period: "", category: "", age_group: "", zone: "" });
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/analytics/filters").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const q = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/analytics", { params: q }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [f]);

  const gkkOpts = useMemo(() => (opts?.gkks || []).filter((g) => !f.zone || g.zone === f.zone), [opts, f.zone]);
  const units = useMemo(() => opts?.gkks.find((g) => g.gkk_id === f.gkk_id)?.units || [], [opts, f.gkk_id]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v, ...(k === "gkk_id" ? { unit_id: "" } : {}), ...(k === "zone" ? { gkk_id: "", unit_id: "" } : {}) }));

  return (
    <div>
      <PageHeader eyebrow="Automated survey analysis" title={t("nav_realities")} testId="realities-header">
        Aggregate, consented survey findings. No household can be identified here: small groups (fewer than 5) are hidden and missing answers are shown separately from zero.
      </PageHeader>
      {opts && (
        <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-6" data-testid="filters-bar">
          <Filter id="zone" value={f.zone} onChange={set("zone")} placeholder="zones" allLabel={t("all")} options={(opts.zones || []).map((z) => ({ value: z, label: z }))} />
          <Filter id="gkk" value={f.gkk_id} onChange={set("gkk_id")} placeholder="GKKs" allLabel={t("all")} options={gkkOpts.map((g) => ({ value: g.gkk_id, label: g.name }))} />
          {units.length > 0 && <Filter id="unit" value={f.unit_id} onChange={set("unit_id")} placeholder="Units" allLabel={t("all")} options={units.map((u) => ({ value: u.unit_id, label: u.name }))} />}
          <Filter id="period" value={f.period} onChange={set("period")} placeholder="periods" allLabel={t("all")} options={opts.periods.map((p) => ({ value: p, label: p }))} />
          <Filter id="category" value={f.category} onChange={set("category")} placeholder="household types" allLabel={t("all")} options={opts.categories.map((c) => ({ value: c, label: label(c) }))} />
          <Filter id="age" value={f.age_group} onChange={set("age_group")} placeholder="age ranges" allLabel={t("all")} options={opts.age_groups.map((a) => ({ value: a, label: `Has members ${label(a).toLowerCase()}` }))} />
        </div>
      )}
      {d && (
        <>
          <p className="mb-6 text-sm text-stone-700" data-testid="filtered-household-count">
            <b className="font-mono-data text-amber-950">{d.totals.households}</b> households match these filters · data dates {d.data_source.earliest_survey_date || "—"} to {d.data_source.latest_survey_date || "—"}
            {d.data_source.outdated && <span className="ml-2 text-amber-800">(outdated)</span>}
          </p>
          {d.suppressed_all && <p className="rounded-lg bg-stone-100 p-4 text-sm text-stone-600" data-testid="suppressed-all-notice">Fewer than 5 households match. Results are hidden to protect privacy.</p>}
          {d.totals.households === 0 && <p className="rounded-lg bg-stone-100 p-4 text-sm text-stone-600" data-testid="no-data-notice">No survey records match these filters.</p>}
          <Tabs defaultValue="insights">
            <TabsList className="mb-6 flex h-auto flex-wrap justify-start bg-[#F5F0E6]">
              <TabsTrigger value="insights" data-testid="tab-insights">Findings</TabsTrigger>
              {SECTIONS.map((s) => <TabsTrigger key={s.id} value={s.id} data-testid={`tab-${s.id}`}>{s.title}</TabsTrigger>)}
              <TabsTrigger value="trends" data-testid="tab-trends">Over time</TabsTrigger>
              <TabsTrigger value="participation" data-testid="tab-participation">Participation</TabsTrigger>
            </TabsList>
            <TabsContent value="insights"><Insights items={d.insights} caveat={d.caveat} /></TabsContent>
            {SECTIONS.map((s) => (
              <TabsContent key={s.id} value={s.id}>
                {!d.distributions.concerns && <p className="rounded-lg bg-stone-100 p-4 text-sm text-stone-600">Charts are hidden: too few households match to show results safely.</p>}
                <div className="grid gap-6 lg:grid-cols-2">
                  {s.charts.map(([k, title]) => <DistChart key={k} title={title} dist={d.distributions[k]} testId={`chart-${k}`} />)}
                </div>
              </TabsContent>
            ))}
            <TabsContent value="trends"><Trends trends={d.trends} /></TabsContent>
            <TabsContent value="participation"><Participation part={d.participation} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
